import React, { forwardRef } from "react";
import styles from "./A5Invoice.module.css";
import * as InvoiceHelper from "helpers/InvoiceHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import { APP_VERSION, TAX_PREFERENCES, CHARGE_CATEGORIES, INVOICE_HEADERS } from "const";
import { QRCodeSVG } from "qrcode.react";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { generateUpiUrl } from "helpers/UpiHelper";
import { useInvoicePrintData, renderTaxRows } from "./PrintInvoiceUtils";

const A5Invoice = forwardRef(({ sale = {}, shop = {}, titles = [] }, ref) => {
  const {
    terminalSettings,
    showMRP,
    paymentModeOnInvoice,
    printTotalSave,
    date,
    payable,
    tax,
    invoiceFormat,
    showHSN,
    showToken,
    showItemCharges,
    isGstInvoice,
    headerRowSpan,
    totalSavings,
    displayTotalCharges,
    displayTotalDiscount,
    chargesLabel,
    showPaymentQr,
  } = useInvoicePrintData(sale, shop);

  return (
    <div ref={ref} className={styles.a5Wrapper}>
      <div className={styles.container}>
        <div className={styles.topHeader}>
          <div className={styles.topLeft}>
            {shop?.shop_logo && <img src={shop.shop_logo} alt="Shop Logo" className={styles.shopLogo} />}
          </div>
          <div className={styles.topRight}>
            <h2 className={styles.subShopName}>{invoiceFormat?.shop_name}</h2>
            <div className={styles.shopDetails}>
              {invoiceFormat?.address_1 && <span>{invoiceFormat.address_1}</span>}
              {invoiceFormat?.address_2 && <span>{invoiceFormat.address_2}</span>}
              {invoiceFormat?.tel_no && <span>Phone: {invoiceFormat.tel_no}</span>}
              {shop?.email && <span>Email: {shop.email}</span>}
              {invoiceFormat?.tin && <span>GSTIN: {invoiceFormat.tin}</span>}
            </div>
          </div>
        </div>

        <div className={styles.headerSeparator}></div>

        <div className={styles.titleRow}>
          <h3 className={styles.invoiceTitle}>
            {sale?.bill_no ? invoiceFormat?.header || INVOICE_HEADERS.TAX_INVOICE : INVOICE_HEADERS.ESTIMATE}
          </h3>
        </div>

        {titles.length > 0 && (
          <div className={styles.titlesRow} style={{ textAlign: "center", marginBottom: "10px" }}>
            {titles.map((title, index) => (
              <h2 key={index} className={styles.invoiceTitle}>
                {title}
              </h2>
            ))}
          </div>
        )}

        <div className={styles.infoBox}>
          <div className={styles.infoLeft}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Customer :</span>
              <div className={styles.shopDetails}>
                {sale?.customer?.name && <div>{sale?.customer?.name}</div>}
                {sale?.customer?.mobile && <div>{sale?.customer?.mobile}</div>}
                {sale?.customer?.location && <div>{sale?.customer?.location}</div>}
                {sale?.customer?.trn_number && <div>GSTIN: {sale?.customer?.trn_number}</div>}
              </div>
            </div>
          </div>

          <div className={styles.infoDivider}></div>

          <div className={styles.infoRight}>
            <div className={styles.infoRow}>
              <span className={styles.label}>{sale?.estimate_no ? "Estimate No :" : "Invoice No :"}</span>
              <span className={styles.val}>{sale?.bill_no}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Date :</span>
              <span className={styles.val}>{DateTimeHelpers.convertDateToDMY(date)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Shift ID :</span>
              <span className={styles.val}>{sale?.shift?.id}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Time :</span>
              <span className={styles.val}>{date.toLocaleTimeString()}</span>
            </div>
            {sale?.order_type && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Order Type :</span>
                <span className={styles.val}>{sale?.order_type}</span>
              </div>
            )}
            {showToken && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Token No :</span>
                <span className={styles.val}>{sale?.token_no}</span>
              </div>
            )}
          </div>
        </div>

        <table className={styles.gridTable}>
          <thead>
            <tr>
              <th rowSpan={headerRowSpan} className={styles.thSN}>
                SN
              </th>
              <th rowSpan={headerRowSpan} className={styles.thDesc}>
                Item Description
              </th>
              {showHSN && (
                <th rowSpan={headerRowSpan} className={styles.thHSN}>
                  HSN
                </th>
              )}
              {showMRP && (
                <th rowSpan={headerRowSpan} className={styles.thMRP}>
                  MRP
                </th>
              )}
              <th rowSpan={headerRowSpan} className={styles.thQty}>
                Qty
              </th>
              <th rowSpan={headerRowSpan} className={styles.thRate}>
                Rate
              </th>

              {isGstInvoice && (
                <>
                  <th colSpan={2} className={styles.center}>
                    CGST
                  </th>
                  <th colSpan={2} className={styles.center}>
                    SGST
                  </th>
                </>
              )}

              <th rowSpan={headerRowSpan} className={styles.thTotal}>
                Total
              </th>
            </tr>

            {isGstInvoice && (
              <tr>
                <th className={styles.center}>%</th>
                <th className={styles.center}>Amt</th>
                <th className={styles.center}>%</th>
                <th className={styles.center}>Amt</th>
              </tr>
            )}
          </thead>
          <tbody>
            {(sale?.items || []).map((it, index) => {
              const halfTax = (it.tax || 0) / 2;
              const halfTaxPercent = (it.taxPercentage || 0) / 2;
              const colsBeforeTotal = 4 + (showHSN ? 1 : 0) + (showMRP ? 1 : 0) + (isGstInvoice ? 4 : 0);

              return (
                <React.Fragment key={it.id || index}>
                  <tr>
                    <td className={styles.center}>{index + 1}</td>
                    <td className={styles.left}>
                      {it.name} {it.option?.label ? `- ${it.option.label}` : ""} {it.note ? `[${it.note}]` : ""}
                    </td>
                    {showHSN && <td className={styles.center}>{it.item.hsn}</td>}
                    {showMRP && <td className={styles.center}>{it.mrp || "-"}</td>}
                    <td className={styles.center}>{it.qty}</td>
                    <td className={styles.right}>
                      {NumberFormatHelper.roundOff(
                        InvoiceHelper.getDisplayRate(it, terminalSettings.invoiceDisplayRate),
                      )}
                    </td>

                    {isGstInvoice ? (
                      <>
                        <td className={styles.center}>{NumberFormatHelper.roundOff(halfTaxPercent)}%</td>
                        <td className={styles.right}>{NumberFormatHelper.roundOff(halfTax)}</td>
                        <td className={styles.center}>{NumberFormatHelper.roundOff(halfTaxPercent)}%</td>
                        <td className={styles.right}>{NumberFormatHelper.roundOff(halfTax)}</td>
                      </>
                    ) : (
                      (shop?.tax_preference !== TAX_PREFERENCES.NO_TAX && <></>) || null
                    )}
                    <td className={styles.right}>{NumberFormatHelper.roundOff(it.totalBeforeTax)}</td>
                  </tr>
                  {showItemCharges &&
                    it.charges &&
                    it.charges.length > 0 &&
                    it.charges.map((charge, cIdx) => (
                      <tr key={`charge-${index}-${cIdx}`}>
                        <td colSpan={colsBeforeTotal} className={styles.right}>
                          {charge.name || "Charge"}
                          {charge.category === CHARGE_CATEGORIES.DISCOUNT ? " (-)" : ""}
                        </td>
                        <td className={styles.right}>{NumberFormatHelper.roundOff(charge.value || 0)}</td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {displayTotalDiscount > 0 && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>Total Discounts</div>
            <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(displayTotalDiscount)}</div>
          </div>
        )}
        {displayTotalCharges > 0 && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>{chargesLabel}</div>
            <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(displayTotalCharges)}</div>
          </div>
        )}

        {renderTaxRows(tax, shop, styles)}

        <div className={styles.summaryGrid}>
          <div className={styles.summaryLabel}>Sub Total</div>
          <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(payable)}</div>
        </div>

        {printTotalSave && totalSavings > 0 && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>You Saved</div>
            <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(totalSavings)}</div>
          </div>
        )}

        {sale?.roundOff !== 0 && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>Rounding</div>
            <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(sale?.roundOff || 0)}</div>
          </div>
        )}

        {(sale?.due || 0) > 0 && (
          <div className={`${styles.summaryGrid} ${styles.boldRow}`}>
            <div className={styles.summaryLabel}>Due Amount</div>
            <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(sale?.due)}</div>
          </div>
        )}

        {paymentModeOnInvoice && (sale?.payment_modes || []).length > 0 && (
          <>
            {sale.payment_modes.map((paymentMode, idx) => (
              <div key={idx} className={styles.summaryGrid}>
                <div className={styles.summaryLabel}>{paymentMode.name || "Payment"}</div>
                <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(paymentMode.amount || 0)}</div>
              </div>
            ))}
          </>
        )}

        {Number(sale?.customer_credit_used || 0) !== 0 && paymentModeOnInvoice && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>Customer Credit</div>
            <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(sale?.customer_credit_used)}</div>
          </div>
        )}

        {Number(sale?.customer_credit_used || 0) !== 0 && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>Previous Balance</div>
            <div className={styles.summaryValue}>
              {NumberFormatHelper.customerAmountFormat(sale?.customer?.balance + sale?.customer_credit_used)}
            </div>
          </div>
        )}

        {sale?.customer && (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryLabel}>Customer Balance</div>
            <div className={styles.summaryValue}>
              {NumberFormatHelper.customerAmountFormat(sale?.customer?.balance)}
            </div>
          </div>
        )}

        <div className={styles.amountInWords}>
          Amount in words: <strong>{NumberFormatHelper.toWords(payable)}</strong>
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.footerLeftSection}>
            <div className={styles.qrBox}>
              {showPaymentQr && (
                <div className={styles.qrSection}>
                  <div className={styles.qrWrapper}>
                    <QRCodeSVG
                      value={generateUpiUrl({
                        upiId: terminalSettings?.upi_id,
                        name: shop?.shop_name,
                        amount: payable,
                      })}
                      size={60}
                    />
                    <p className={styles.qrText}>Scan to Pay</p>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.messageBox}>
              {invoiceFormat?.note && <p className={styles.noteText}>{invoiceFormat.note}</p>}
            </div>

            {showPaymentQr && (
              <div className={styles.qrSection}>
                <div className={styles.qrWrapper}>
                  <QRCodeSVG
                    value={generateUpiUrl({
                      upiId: terminalSettings?.upi_id,
                      name: shop?.shop_name,
                      amount: payable,
                    })}
                    size={60}
                  />
                  <p className={styles.qrText}>Scan to Pay</p>
                </div>
              </div>
            )}
          </div>

          <div className={styles.signatureBox}>
            <div className={styles.sigTitle}>Authorized Signature</div>
          </div>
        </div>

        <div className={styles.footer}>
          {invoiceFormat?.footer_1 && <p>{invoiceFormat.footer_1}</p>}
          {invoiceFormat?.footer_2 && <p>{invoiceFormat.footer_2}</p>}
        </div>

        <div className={styles.versionInfo}>w{APP_VERSION}</div>
      </div>
    </div>
  );
});

A5Invoice.displayName = "A5Invoice";

export default A5Invoice;
